/**
 * RegistrationSettings Component
 * Admin interface for configuring registration system
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './RegistrationSettings.css';

function RegistrationSettings() {
  const [settings, setSettings] = useState({
    enabled: true,
    adminEmails: '',
    autoApproveEnabled: false,
    autoApproveDomains: '',
    emailNotificationsEnabled: true,
    studentAccessDays: 180
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [testEmail, setTestEmail] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/registration/settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (message.text) setMessage({ type: '', text: '' });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    const studentAccessDays = Number(settings.studentAccessDays);
    if (!Number.isInteger(studentAccessDays) || studentAccessDays < 1 || studentAccessDays > 730) {
      setMessage({ type: 'error', text: 'Student account length must be a whole number of days between 1 and 730' });
      setSaving(false);
      return;
    }

    // Validate admin emails
    if (settings.adminEmails) {
      const emails = settings.adminEmails.split(',').map(e => e.trim()).filter(e => e);
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      
      for (const email of emails) {
        if (!emailRegex.test(email)) {
          setMessage({ type: 'error', text: `Invalid email format: ${email}` });
          setSaving(false);
          return;
        }
      }
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/registration/settings`,
        { ...settings, studentAccessDays },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to save settings' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      setMessage({ type: 'error', text: 'Please enter an email address to test' });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmail)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    setTestingEmail(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/registration/test-email`,
        { email: testEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setMessage({ type: 'success', text: `Test email sent to ${testEmail}! Check your inbox.` });
        setTestEmail('');
      } else {
        setMessage({ type: 'error', text: response.data.message });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to send test email' 
      });
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading settings...</div>;
  }

  return (
    <div className="registration-settings">
      <h2>Registration Settings</h2>
      
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSave}>
        {/* Registration Enabled */}
        <div className="settings-section">
          <h3>General Settings</h3>
          
          <div className="setting-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="enabled"
                checked={settings.enabled}
                onChange={handleChange}
              />
              <span>Enable user registration</span>
            </label>
            <p className="setting-description">
              Allow users to request accounts via the login page
            </p>
          </div>
        </div>

        {/* Email Settings */}
        <div className="settings-section">
          <h3>Email Notifications</h3>
          
          <div className="setting-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="emailNotificationsEnabled"
                checked={settings.emailNotificationsEnabled}
                onChange={handleChange}
              />
              <span>Enable email notifications</span>
            </label>
            <p className="setting-description">
              Send emails for registration events
            </p>
          </div>

          <div className="setting-item">
            <label htmlFor="adminEmails">Admin Notification Emails</label>
            <input
              type="text"
              id="adminEmails"
              name="adminEmails"
              value={settings.adminEmails}
              onChange={handleChange}
              placeholder="admin1@example.com, admin2@example.com"
            />
            <p className="setting-description">
              Comma-separated list of emails to notify when users register
            </p>
          </div>
        </div>

        {/* Auto-Approval Settings */}
        <div className="settings-section">
          <h3>Auto-Approval</h3>
          
          <div className="setting-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="autoApproveEnabled"
                checked={settings.autoApproveEnabled}
                onChange={handleChange}
              />
              <span>Enable auto-approval</span>
            </label>
            <p className="setting-description">
              Automatically approve registrations from trusted email domains
            </p>
          </div>

          <div className="setting-item">
            <label htmlFor="autoApproveDomains">Auto-Approve Domains</label>
            <input
              type="text"
              id="autoApproveDomains"
              name="autoApproveDomains"
              value={settings.autoApproveDomains}
              onChange={handleChange}
              placeholder="yourhospital.com, yourorg.com"
              disabled={!settings.autoApproveEnabled}
            />
            <p className="setting-description">
              Comma-separated list of email domains to auto-approve
            </p>
          </div>
        </div>

        {/* Student Account Settings */}
        <div className="settings-section">
          <h3>Student Accounts</h3>

          <div className="setting-item">
            <label htmlFor="studentAccessDays">Student account length (days)</label>
            <input
              type="number"
              id="studentAccessDays"
              name="studentAccessDays"
              min="1"
              max="730"
              step="1"
              value={settings.studentAccessDays}
              onChange={handleChange}
            />
            <p className="setting-description">
              {Number(settings.studentAccessDays) >= 1 && `About ${(Number(settings.studentAccessDays) / 30.4).toFixed(1)} months. `}
              Self-registered student accounts are deactivated automatically this many days after signup.
              Applies to new student signups only — to change an existing student's date, edit their expiry under Users.
            </p>
          </div>
        </div>

        {/* Save Button */}
        <div className="form-actions">
          <button type="submit" className="btn-save" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>

      {/* Test Email Section */}
      <div className="test-email-section">
        <h3>Test Email Configuration</h3>
        <p>Send a test email to verify your email settings are working correctly.</p>
        
        <div className="test-email-form">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="Enter email address"
            disabled={testingEmail}
          />
          <button 
            type="button"
            onClick={handleTestEmail}
            className="btn-test"
            disabled={testingEmail}
          >
            {testingEmail ? 'Sending...' : 'Send Test Email'}
          </button>
        </div>
      </div>

      {/* Configuration Info */}
      <div className="config-info">
        <h3>📋 Email Configuration</h3>
        <p>Email is sent over SMTP using these server environment variables (set in Render for production):</p>
        <pre>{`EMAIL_HOST=smtp.resend.com
EMAIL_PORT=587
EMAIL_USER=resend
EMAIL_PASSWORD=<Resend API key>
EMAIL_FROM=noreply@yourdomain.com
EMAIL_REPLY_TO=an-inbox-you-read@example.com
CLIENT_URL=https://your-app-address`}</pre>
        <p className="info-note">
          <strong>Note:</strong> The EMAIL_FROM domain must be verified in Resend (SPF/DKIM DNS records),
          or receiving mail servers — hospital filters especially — may silently hold the message.
        </p>
      </div>
    </div>
  );
}

export default RegistrationSettings;
